/**
 * useMapStore v2 — single zustand store for the /map state machine.
 *
 * Spec: docs/superpowers/specs/2026-05-07-map-state-machine.md (v2)
 *   §2  State shape (single source of truth — no useCoverageStore satellite)
 *   §3  Actions (dispatch-only API; no per-field setters)
 *   §10 Persistence — partialize to PersistentMapState only
 *
 * Plan: docs/superpowers/plans/2026-05-07-sprint-2026-21-map-refactor.md Task 3
 *
 * Founder lock-ins (architectural commitments — do not relax without ADR):
 *   #1 Persistence subset is exactly PersistentMapState (basemap, cropFillOn,
 *      labels, contourMode, legendPosition). Session vars (activeRasterMode,
 *      selectedDate, activeScope, coverage, availableDates, status, panel
 *      flags) are intentionally NOT persisted — they reset on reload so the
 *      user always lands on a clean satellite state.
 *   #2 No per-field setter API. Components dispatch typed actions, the
 *      reducer is the only writer. Direct setState is reserved for tests
 *      and effect hooks that need atomic multi-field writes.
 *
 * NOTE on rehydration: zustand's persist middleware merges the persisted
 * subset into the initial state on storage rehydrate. We do NOT route that
 * merge through a HYDRATE dispatch — the merge is structurally equivalent
 * and avoids a redundant reducer pass. The HYDRATE action remains for
 * programmatic restore from non-localStorage sources (e.g. server-side
 * user prefs sync via /api/v1/users/me/map-preferences).
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { mapReducer, INITIAL_MAP_STATE } from './state/mapReducer';
import type { MapState, MapAction, PersistentMapState } from './state/mapState';

interface MapStoreShape extends MapState {
  dispatch: (action: MapAction) => void;
}

/**
 * The exact set of keys persisted to localStorage. Adding a key here is a
 * deliberate decision — it survives reload, multi-tab, and incognito-restore.
 * Anything not listed is session-only by construction (partialize below).
 */
const PERSISTENT_KEYS: (keyof PersistentMapState)[] = [
  'basemap',
  'cropFillOn',
  'terrainOn', // AK-211: слой «Рельеф» переживает reload (как cropFillOn)
  'labels',
  'contourMode',
  'legendPosition',
  'satelliteStripPosition',
];

export const useMapStore = create<MapStoreShape>()(
  persist(
    (set) => ({
      ...INITIAL_MAP_STATE,
      dispatch: (action) => set((s) => mapReducer(s, action)),
    }),
    {
      name: 'map-v2-state',
      storage: createJSONStorage(() => localStorage),
      // Whitelist persistent keys only. Object.fromEntries gives us a
      // structural Partial<MapStoreShape> that zustand merges into the
      // initial state on rehydrate.
      partialize: (state) =>
        Object.fromEntries(
          PERSISTENT_KEYS.map((k) => [k, state[k]]),
        ) as Partial<MapStoreShape>,
    },
  ),
);
